import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, Tag, AlertCircle, RefreshCw, FileText, Ticket, Bed } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import apiClient from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { useHotel } from '../context/HotelContext';

const STATUS_STYLES = {
  Hold:       'bg-yellow-100 text-yellow-800',
  Confirmed:  'bg-green-100 text-green-800',
  Cancelled:  'bg-red-100 text-red-800',
  CheckedIn:  'bg-blue-100 text-blue-800',
  CheckedOut: 'bg-gray-100 text-gray-800',
  NoShow:     'bg-orange-100 text-orange-800',
};

const toUtcDate = (str) => {
  if (!str) return new Date(0);
  return new Date(str.endsWith('Z') ? str : str + 'Z');
};

const CountdownTimer = ({ expiresAt }) => {
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    const tick = () => {
      const diff = toUtcDate(expiresAt) - new Date();
      if (diff <= 0) { setRemaining('Expired'); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(`${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return <span className="font-mono text-yellow-700">{remaining}</span>;
};

const MyBookings = () => {
  const { isLoggedIn } = useAuth();
  const { hotel } = useHotel();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('rooms');
  const [bookings, setBookings] = useState([]);
  const [ticketOrders, setTicketOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(null);
  const [cancelError, setCancelError] = useState('');

  useEffect(() => {
    if (!isLoggedIn) { navigate('/login', { state: { from: '/my-bookings' } }); }
  }, [isLoggedIn, navigate]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [roomData, ticketData] = await Promise.allSettled([
        apiClient.get('/api/Book/MyBookings'),
        apiClient.get('/api/Ticket/MyOrders'),
      ]);
      setBookings(roomData.status === 'fulfilled' ? (roomData.value || []) : []);
      setTicketOrders(ticketData.status === 'fulfilled' ? (ticketData.value || []) : []);
    } catch (err) {
      setError(err.message || 'Failed to load bookings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) fetchBookings();
  }, [isLoggedIn, fetchBookings]);

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Cancel this booking?')) return;
    setCancelling(bookingId);
    setCancelError('');
    try {
      await apiClient.post(`/api/Book/Cancel/${bookingId}`);
      setBookings(prev =>
        prev.map(b => b.bookingId === bookingId ? { ...b, status: 'Cancelled' } : b)
      );
    } catch (err) {
      setCancelError(err.message || 'Cancellation failed.');
    } finally {
      setCancelling(null);
    }
  };

  if (!isLoggedIn) return null;

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="container max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Bookings</h1>
          <Button onClick={fetchBookings} variant="outline" className="flex items-center gap-2">
            <RefreshCw size={16} /> Refresh
          </Button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('rooms')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${activeTab === 'rooms' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}
          >
            <Bed size={14} /> Rooms ({bookings.length})
          </button>
          {hotel?.isTicketEnabled && (
            <button
              onClick={() => setActiveTab('tickets')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${activeTab === 'tickets' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}
            >
              <Ticket size={14} /> Tickets ({ticketOrders.length})
            </button>
          )}
        </div>

        {cancelError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertCircle size={16} /> {cancelError}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-16">
            <AlertCircle className="mx-auto mb-3 text-red-500" size={40} />
            <p className="text-gray-600">{error}</p>
          </div>
        )}

        {/* Room bookings */}
        {activeTab === 'rooms' && !loading && !error && bookings.length === 0 && (
          <div className="text-center py-16">
            <Calendar className="mx-auto mb-3 text-gray-300" size={48} />
            <p className="text-gray-500 text-lg">No room bookings yet.</p>
            <Button className="mt-4" onClick={() => navigate('/rooms')}>Browse Rooms</Button>
          </div>
        )}

        {activeTab === 'rooms' && (
        <div className="space-y-4">
          {bookings.map(b => (
            <Card key={b.bookingId} className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{b.roomTypeName}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[b.status] || 'bg-gray-100 text-gray-700'}`}>
                      {b.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm text-gray-600">
                    <div className="flex items-center gap-1.5">
                      <Tag size={14} className="text-blue-500" />
                      <span className="font-mono font-medium text-gray-800">{b.pnr}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar size={14} className="text-blue-500" />
                      <span>{new Date(b.checkInDate).toLocaleDateString()} → {new Date(b.checkOutDate).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users size={14} className="text-blue-500" />
                      <span>{b.numberOfRooms > 1 ? `${b.numberOfRooms} rooms · ` : ''}{b.adults} adult{b.adults !== 1 ? 's' : ''}{b.children > 0 ? `, ${b.children} child${b.children !== 1 ? 'ren' : ''}` : ''}</span>
                    </div>
                  </div>

                  {b.status === 'Hold' && b.holdExpiresAt && (
                    <div className="mt-3 flex items-center gap-1.5 text-sm bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                      <Clock size={14} className="text-yellow-600" />
                      <span className="text-yellow-700">Hold expires in: </span>
                      <CountdownTimer expiresAt={b.holdExpiresAt} />
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-end gap-3 min-w-[140px]">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600">৳{Number(b.grandTotal).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{b.nights} night{b.nights !== 1 ? 's' : ''}{b.numberOfRooms > 1 ? ` · ${b.numberOfRooms} rooms` : ''} · {b.currency}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/my-bookings/${b.bookingId}`)}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50 text-xs flex items-center gap-1"
                  >
                    <FileText size={13} /> View Details
                  </Button>
                  {(b.status === 'Hold' || b.status === 'Confirmed') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancel(b.bookingId)}
                      disabled={cancelling === b.bookingId}
                      className="text-red-600 border-red-300 hover:bg-red-50 text-xs"
                    >
                      {cancelling === b.bookingId ? 'Cancelling...' : 'Cancel'}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
        )}

        {/* Ticket orders */}
        {activeTab === 'tickets' && !loading && ticketOrders.length === 0 && (
          <div className="text-center py-16">
            <Ticket className="mx-auto mb-3 text-gray-300" size={48} />
            <p className="text-gray-500 text-lg">No ticket orders yet.</p>
            <Button className="mt-4" onClick={() => navigate('/tickets')}>Browse Tickets</Button>
          </div>
        )}

        {activeTab === 'tickets' && (
          <div className="space-y-4">
            {ticketOrders.map(o => (
              <Card key={o.id} className={`p-5 ${o.isCancelled ? 'opacity-60' : ''}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="font-mono font-bold text-gray-900">{o.orderRef}</span>
                      {o.isCancelled
                        ? <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Cancelled</span>
                        : <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${o.paymentStatus === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{o.paymentStatus}</span>
                      }
                    </div>
                    <p className="text-gray-700 text-sm mb-1">{o.items.map(i => `${i.quantity}× ${i.ticketTypeName}`).join(', ')}</p>
                    <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1"><Calendar size={13} className="text-blue-500" />Visit: {o.visitDate}</span>
                      <span className="flex items-center gap-1"><Tag size={13} className="text-blue-500" />{o.items.reduce((s,i)=>s+i.quantity,0)} tickets</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 min-w-[130px]">
                    <p className="text-2xl font-bold text-blue-600">৳{Number(o.totalAmount).toLocaleString()}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/tickets/order/${o.orderRef}`)}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50 text-xs flex items-center gap-1"
                    >
                      <Ticket size={13} /> View & Print
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBookings;
